import {
  SBState,
  SBSelectRaw,
  getIdOrModelId,
  baseReducers,
  getSlugOrModelSlug,
} from '../helpers/ReducerHelper'
import {
  SBAPICreate,
  SBAPIDelete,
  SBAPIFetch,
  SBAPIFetchDispatch,
  SBAPIFetchPaginatedDispatch,
  SBAPIUpdate,
} from '../helpers/SBAPIHelper'
import { Profile } from '../models/Profile'
import { AppDispatch, RootState } from './store'
import { TableParams } from '../models/TableParams'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { PROFILE_URL } from '../helpers/urls'
import { profileSchema } from '../models/schema'

const initialState: SBState<Profile> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: {
    pagination: {
      current: 1,
      pageSize: 10,
    },
  },
}

const slice = createSlice({
  name: 'profile',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getProfilesSuccess,
  setQuery: setProfileQuery,
  reset: resetProfileState,
  resetQueryAndIds: resetProfileQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<{ [key: string]: Profile }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectProfiles = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedProfile = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectProfileById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectProfilesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedProfile =
  (profile: Profile | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Profile>(profile)))

export const setSelectedProfileWithSlug =
  (profile: Profile | string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    const slug = getSlugOrModelSlug<Profile>(profile)
    const selectedProfile = Object.values(state[slice.name].items).find(
      (item: Profile) => item.slug === slug
    )

    if (selectedProfile?.id) {
      dispatch(slice.actions.setSelectedId(selectedProfile.id))
    } else {
      console.warn(`Profile with slug "${slug}" not found or missing ID.`)
    }
  }

export const getProfiles = (params: TableParams) =>
  SBAPIFetchPaginatedDispatch<Profile>(
    PROFILE_URL,
    params,
    [profileSchema],
    slice.actions
  )

export const getProfileWithSlug = (slug: string) =>
  SBAPIFetchDispatch<Profile>(
    `${PROFILE_URL}/${slug}`,
    profileSchema,
    slice.actions
  )

export const createProfile = (code: string) =>
  SBAPICreate<{ code: string }>(
    { code },
    PROFILE_URL,
    profileSchema,
    slice.actions
  )

export const updateProfile = (profile: Profile) =>
  SBAPIUpdate<Profile>(profile, `${PROFILE_URL}/${profile.id}`, slice.actions)

export const deleteProfile = (profile: Profile) =>
  SBAPIDelete<Profile>(profile, `${PROFILE_URL}/${profile.slug}`, slice.actions)

export const getConnectUrlForInstagram = () =>
  SBAPIFetch<{ url: string }>(`${PROFILE_URL}/connect/instagram`).then(
    (response) => response.data
  )
